import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Info } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { CUSTOM_QUOTE_NEEDS, COMPANY_INFO } from '../data/mock';
import AddressAutocomplete from '../components/AddressAutocomplete';

const PROVINCES = [
  { value: 'ON', label: 'Ontario' },
  { value: 'QC', label: 'Quebec' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'AB', label: 'Alberta' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'YT', label: 'Yukon' },
  { value: 'NU', label: 'Nunavut' }
];

const CustomQuotePage = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    streetAddress: '',
    unit: '',
    city: '',
    province: '',
    postalCode: '',
    needs: [],
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleNeedToggle = (needId) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs.includes(needId)
        ? prev.needs.filter(n => n !== needId)
        : [...prev.needs, needId]
    }));
  };

  const handleAddressSelect = useCallback((addressData) => {
    setFormData(prev => ({
      ...prev,
      streetAddress: addressData.streetAddress,
      city: addressData.city,
      province: addressData.province,
      postalCode: addressData.postalCode
    }));
  }, []);

  const handleStreetChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, streetAddress: value }));
  }, []);

  const canSubmit = () => {
    return formData.businessName && 
           formData.contactName && 
           formData.email && 
           formData.phone && 
           formData.streetAddress &&
           formData.city &&
           formData.province &&
           formData.postalCode &&
           formData.needs.length > 0;
  };

  const getFullAddress = () => {
    const parts = [];
    if (formData.unit) {
      parts.push(`${formData.unit}-${formData.streetAddress}`);
    } else {
      parts.push(formData.streetAddress);
    }
    parts.push(`${formData.city}, ${formData.province} ${formData.postalCode}`);
    return parts.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container section-padding">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 bg-[var(--brand-green)] text-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8" />
            </div>
            <h1 className="hero-medium mb-4">Quote Request Received</h1>
            <p className="body-large mb-10">
              Thank you for your interest in Executive Mat. Our team will review your request 
              and respond within one business day.
            </p>

            <div className="card-clean text-left mb-8">
              <h2 className="heading-2 text-lg mb-5">Request Summary</h2>
              <div className="space-y-4">
                <div>
                  <p className="body-small text-[var(--text-secondary)]">Business</p>
                  <p className="body-regular font-medium">{formData.businessName}</p>
                </div>
                <div>
                  <p className="body-small text-[var(--text-secondary)]">Contact</p>
                  <p className="body-regular">{formData.contactName}</p>
                </div>
                <div>
                  <p className="body-small text-[var(--text-secondary)]">Service Address</p>
                  <p className="body-regular">{getFullAddress()}</p>
                </div>
                <div>
                  <p className="body-small text-[var(--text-secondary)]">Needs</p>
                  <p className="body-regular">
                    {formData.needs.map(needId => 
                      CUSTOM_QUOTE_NEEDS.find(n => n.id === needId)?.label
                    ).join(', ')}
                  </p>
                </div>
              </div>
            </div>

            <Link to="/" className="btn-primary">
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container section-padding">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <Link 
              to="/shop" 
              className="btn-secondary mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Shop
            </Link>
            <h1 className="hero-medium mb-4">Request a Custom Quote</h1>
            <p className="body-large">
              For orders of 5+ mats, custom sizes, different colours, logo mats, or high-traffic locations, 
              our team will create a tailored program for your needs.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="card-clean mb-8">
              <h2 className="heading-2 text-lg mb-6">Business Information</h2>
              
              <div className="space-y-5">
                <div>
                  <label className="block body-regular font-medium mb-2">Business Name *</label>
                  <input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    className="input-clean"
                    placeholder="Your business name"
                    required
                    data-testid="custom-quote-business-name"
                  />
                </div>

                <div>
                  <label className="block body-regular font-medium mb-2">Contact Name *</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="input-clean"
                    placeholder="Primary contact person"
                    required
                    data-testid="custom-quote-contact-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block body-regular font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-clean"
                      placeholder="email@business.com"
                      required
                      data-testid="custom-quote-email"
                    />
                  </div>
                  <div>
                    <label className="block body-regular font-medium mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-clean"
                      placeholder="(555) 123-4567"
                      required
                      data-testid="custom-quote-phone"
                    />
                  </div>
                </div>

                {/* Service Address with Autocomplete */}
                <div className="border-t border-[var(--border-light)] pt-5 mt-6">
                  <h3 className="heading-3 text-base mb-4">Service Address</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block body-regular font-medium mb-2">Street Address *</label>
                      <AddressAutocomplete
                        value={formData.streetAddress}
                        onChange={handleStreetChange}
                        onAddressSelect={handleAddressSelect}
                        placeholder="Start typing your address..."
                      />
                      <p className="text-xs text-[var(--text-light)] mt-1">Start typing to see address suggestions</p>
                    </div>
                    
                    <div>
                      <label className="block body-regular font-medium mb-2">Unit / Suite (Optional)</label>
                      <input
                        type="text"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        className="input-clean"
                        placeholder="Suite 100"
                        data-testid="custom-quote-unit"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block body-regular font-medium mb-2">City *</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="input-clean"
                          placeholder="Toronto"
                          required
                          data-testid="custom-quote-city"
                        />
                      </div>
                      <div>
                        <label className="block body-regular font-medium mb-2">Province *</label>
                        <select
                          value={formData.province}
                          onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                          className="input-clean"
                          required
                          data-testid="custom-quote-province"
                        >
                          <option value="">Select province</option>
                          {PROVINCES.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block body-regular font-medium mb-2">Postal Code *</label>
                        <input
                          type="text"
                          value={formData.postalCode}
                          onChange={(e) => setFormData({ ...formData, postalCode: e.target.value.toUpperCase() })}
                          className="input-clean"
                          placeholder="M5V 1A1"
                          maxLength={7}
                          required
                          data-testid="custom-quote-postal"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-clean mb-8">
              <h2 className="heading-2 text-lg mb-2">What Do You Need? *</h2>
              <p className="body-regular text-[var(--text-secondary)] mb-6">
                Select all that apply to your request:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CUSTOM_QUOTE_NEEDS.map((need) => (
                  <label
                    key={need.id}
                    className={`selectable flex items-center gap-3 p-4 rounded-lg border cursor-pointer ${
                      formData.needs.includes(need.id) ? 'selected' : 'border-[var(--border-light)]'
                    }`}
                  >
                    <Checkbox
                      checked={formData.needs.includes(need.id)}
                      onCheckedChange={() => handleNeedToggle(need.id)}
                    />
                    <span className="body-regular">{need.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="card-clean mb-8">
              <h2 className="heading-2 text-lg mb-2">Additional Notes</h2>
              <p className="body-regular text-[var(--text-secondary)] mb-4">
                Any specific requirements, questions, or details about your mat program:
              </p>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-clean min-h-[120px] resize-none"
                placeholder="Tell us more about your needs (optional)"
                data-testid="custom-quote-notes"
              />
            </div>

            <div className="notice-info mb-8 flex items-start gap-3">
              <Info className="w-5 h-5 text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
              <p className="body-regular text-[var(--text-secondary)]">
                Our team will review your request and respond within one business day with a customized quote.
              </p>
            </div>

            <div className="flex justify-between">
              <Link to="/shop" className="btn-secondary">
                <ArrowLeft className="w-4 h-4" />
                Back to Shop
              </Link>
              <button
                type="submit"
                disabled={!canSubmit() || isSubmitting}
                className="btn-primary"
                data-testid="custom-quote-submit"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                {!isSubmitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomQuotePage;
